import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Hammer, Check, X, Package, MapPin, Star, Plus, Trash2 } from "lucide-react";
import { api, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const STATUS_COLORS = {
  "Awaiting Vendor Confirmation": "bg-amber-100 text-amber-700",
  "Vendor Confirmed": "bg-indigo-100 text-indigo-700",
  "Quote Sent": "bg-blue-100 text-blue-700",
  "Counter Offered": "bg-orange-100 text-orange-700",
  "Approved": "bg-green-100 text-green-700",
  "Confirmed": "bg-green-100 text-green-700",
  "Declined by Vendor": "bg-red-100 text-red-700",
};

function Negotiation({ log }) {
  if (!log?.length) return null;
  return (
    <div className="mt-3 rounded-xl bg-secondary p-3">
      <p className="text-xs font-bold text-midnight mb-2">Negotiation history</p>
      {log.map((n, i) => (
        <div key={i} className="flex items-center justify-between text-xs">
          <span className={`font-semibold ${n.by === "vendor" ? "text-mart-wood" : "text-midnight"}`}>{n.by === "vendor" ? "You" : "Customer"} {n.message ? `— ${n.message}` : ""}</span>
          <span className="font-bold">{inr(n.amount)}</span>
        </div>
      ))}
    </div>
  );
}

function RequestCard({ req, onChange }) {
  const [quote, setQuote] = useState("");
  const status = req.status;

  const act = async (action, extra = {}) => {
    try {
      await api.post(`/vendor/customizations/${req.id}`, { action, ...extra });
      toast.success(action === "quote" ? "Quote sent" : action === "accept_counter" ? "Counter accepted" : action === "confirm" ? "Request confirmed" : "Request declined");
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const canQuote = ["Awaiting Vendor Confirmation", "Vendor Confirmed", "Counter Offered"].includes(status);

  return (
    <div className="rounded-3xl border border-border p-5" data-testid={`vendor-req-${req.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">{new Date(req.created_at).toLocaleString("en-IN")}</p>
        <span className={`rounded-full px-3 py-1 text-xs font-bold ${STATUS_COLORS[status] || "bg-secondary"}`}>{status}</span>
      </div>
      <p className="mt-2 text-sm font-bold text-midnight">{req.name} • {req.phone}</p>
      <p className="mt-1 text-sm text-muted-foreground"><b className="text-midnight">Requirement:</b> {req.details}</p>
      {req.finish && <p className="text-sm text-muted-foreground">Preferred finish: {req.finish}</p>}
      {req.quote != null && <p className="mt-2 text-sm font-bold text-mart-wood">Your last quote: {inr(req.quote)}</p>}
      <Negotiation log={req.negotiation} />

      {status === "Counter Offered" && (
        <div className="mt-3 rounded-2xl bg-orange-50 p-4">
          <p className="text-sm">Customer counter-offered <b className="text-orange-700">{inr(req.counter_amount)}</b></p>
          <button onClick={() => act("accept_counter")} data-testid={`vendor-accept-counter-${req.id}`} className="mt-2 flex items-center gap-1.5 rounded-full bg-mart-home px-4 py-2.5 text-sm font-bold text-white"><Check className="h-4 w-4" /> Accept Counter ({inr(req.counter_amount)})</button>
        </div>
      )}

      {(canQuote || status === "Counter Offered") && (
        <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border pt-4">
          {status === "Awaiting Vendor Confirmation" && (
            <button onClick={() => act("confirm")} data-testid={`vendor-confirm-${req.id}`} className="flex items-center gap-1.5 rounded-full bg-indigo-600 px-4 py-2.5 text-sm font-bold text-white"><Check className="h-4 w-4" /> Confirm Feasibility</button>
          )}
          <div className="flex items-center gap-2">
            <div className="flex items-center rounded-full border border-border bg-white px-3 py-2"><span className="text-sm text-muted-foreground">₹</span><input type="number" value={quote} onChange={(e) => setQuote(e.target.value)} placeholder={status === "Counter Offered" ? "Revised quote" : "Quote amount"} data-testid={`vendor-quote-input-${req.id}`} className="ml-1 w-28 bg-transparent text-sm outline-none" /></div>
            <button onClick={() => act("quote", { quote: Number(quote) })} data-testid={`vendor-send-quote-${req.id}`} className="rounded-full bg-mart-wood px-4 py-2.5 text-sm font-bold text-white">{status === "Counter Offered" ? "Send Revised Quote" : "Send Quote"}</button>
          </div>
          {status !== "Counter Offered" && <button onClick={() => act("decline")} data-testid={`vendor-decline-${req.id}`} className="flex items-center gap-1.5 rounded-full border border-border px-4 py-2.5 text-sm font-bold text-red-600"><X className="h-4 w-4" /> Decline</button>}
        </div>
      )}
      {status === "Quote Sent" && <p className="mt-3 text-sm text-muted-foreground">Waiting for customer to approve…</p>}
      {(status === "Approved" || status === "Confirmed") && <p className="mt-3 text-sm font-semibold text-mart-home">✓ Customer approved — proceed with manufacturing.</p>}
    </div>
  );
}

const EMPTY_P = { name: "", category: "Living Room", price: "", images: "", description: "", material: "", finish: "", customizable: true };

function Catalogue({ vendorName }) {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(EMPTY_P);
  const [adding, setAdding] = useState(false);
  const load = () => api.get("/vendor/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name || !form.price) { toast.error("Name and price required"); return; }
    try {
      await api.post("/vendor/products", { ...form, price: Number(form.price), images: form.images ? [form.images] : [] });
      toast.success("Product added to your catalogue"); setForm(EMPTY_P); setAdding(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add"); }
  };
  const del = async (id) => { await api.delete(`/vendor/products/${id}`); toast("Product removed"); load(); };
  const updatePrice = async (id, price) => { await api.put(`/vendor/products/${id}`, { price: Number(price) }); toast.success("Price updated"); load(); };

  return (
    <div data-testid="vendor-catalogue">
      <button onClick={() => setAdding(!adding)} data-testid="vendor-add-product-btn" className="mb-4 flex items-center gap-2 rounded-full bg-midnight px-5 py-2.5 text-sm font-bold text-white"><Plus className="h-4 w-4" /> {adding ? "Cancel" : "Add Product"}</button>
      {adding && (
        <div className="mb-6 grid sm:grid-cols-2 gap-3 rounded-3xl border border-border p-6">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Product name" data-testid="vp-name" className="rounded-xl border border-border px-4 py-3 text-sm outline-none focus:border-gold" />
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-xl border border-border px-4 py-3 text-sm">
            {["Living Room", "Bedroom", "Dining", "Office"].map((c) => <option key={c}>{c}</option>)}
          </select>
          <input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="Starting price ₹" data-testid="vp-price" className="rounded-xl border border-border px-4 py-3 text-sm outline-none focus:border-gold" />
          <input value={form.material} onChange={(e) => setForm({ ...form, material: e.target.value })} placeholder="Material (e.g. Teak)" className="rounded-xl border border-border px-4 py-3 text-sm outline-none focus:border-gold" />
          <input value={form.images} onChange={(e) => setForm({ ...form, images: e.target.value })} placeholder="Image URL" className="rounded-xl border border-border px-4 py-3 text-sm outline-none focus:border-gold sm:col-span-2" />
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" className="rounded-xl border border-border px-4 py-3 text-sm outline-none focus:border-gold sm:col-span-2" rows={2} />
          <label className="flex items-center gap-2 text-sm sm:col-span-2"><input type="checkbox" checked={form.customizable} onChange={(e) => setForm({ ...form, customizable: e.target.checked })} className="accent-gold" /> Customizable (customers can request customization)</label>
          <button onClick={create} data-testid="vp-save" className="rounded-full bg-gold py-3 font-bold text-midnight sm:col-span-2">Save Product</button>
        </div>
      )}
      {products.length === 0 ? <p className="text-muted-foreground">No products yet. Add your first catalogue item.</p> : (
        <div className="space-y-2">
          {products.map((pr) => (
            <div key={pr.id} className="flex items-center gap-3 rounded-2xl border border-border p-3" data-testid={`vp-item-${pr.slug}`}>
              <img src={pr.images?.[0] || "https://images.unsplash.com/photo-1633505899118-4ca6bd143043?w=200"} alt="" className="h-14 w-14 rounded-lg object-cover bg-secondary" />
              <div className="flex-1"><p className="text-sm font-bold text-midnight line-clamp-1">{pr.name}</p><p className="text-xs text-muted-foreground">{pr.category}{pr.customizable && " • Customizable"}</p></div>
              <div className="flex items-center rounded-full border border-border px-2 py-1"><span className="text-xs text-muted-foreground">₹</span><input type="number" defaultValue={pr.price} onBlur={(e) => e.target.value != pr.price && updatePrice(pr.id, e.target.value)} className="w-20 bg-transparent text-sm outline-none" data-testid={`vp-price-${pr.slug}`} /></div>
              <button onClick={() => del(pr.id)} data-testid={`vp-delete-${pr.slug}`} className="text-red-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function VendorPortal() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [requests, setRequests] = useState([]);
  const [tab, setTab] = useState("requests");

  const loadReq = () => api.get("/vendor/customizations").then((r) => setRequests(r.data)).catch(() => {});

  useEffect(() => {
    if (loading) return;
    if (!user?.user_id || user.role !== "vendor") { navigate("/login"); return; }
    api.get("/vendor/me").then((r) => setData(r.data)).catch(() => {});
    loadReq();
  }, [user, loading, navigate]);

  if (loading || !data) return <div className="py-32 text-center"><div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-gold border-t-transparent" /></div>;

  const pending = requests.filter((r) => ["Awaiting Vendor Confirmation", "Vendor Confirmed", "Counter Offered"].includes(r.status)).length;
  const p = data.profile || {};

  return (
    <div className="mx-auto max-w-[1200px] px-4 md:px-8 py-8">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <span className="text-xs font-bold uppercase tracking-widest text-mart-wood">Wood Mart Vendor Portal</span>
          <h1 className="text-3xl font-extrabold text-midnight">{data.user.name}</h1>
          <p className="text-sm text-muted-foreground flex items-center gap-3">
            {p.city && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{p.city}</span>}
            {p.rating && <span className="flex items-center gap-1"><Star className="h-3.5 w-3.5 fill-gold text-gold" />{p.rating}</span>}
          </p>
        </div>
        <button onClick={logout} data-testid="vendor-logout-btn" className="rounded-full border border-border px-5 py-2.5 text-sm font-bold text-red-600">Logout</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[[Hammer, "Pending Actions", pending], [Check, "Approved", requests.filter((r) => r.status === "Approved").length], [Package, "In Negotiation", requests.filter((r) => r.status === "Counter Offered").length], [Hammer, "Total Requests", requests.length]].map(([Icon, l, v], i) => (
          <div key={i} className="rounded-3xl border border-border p-5"><Icon className="h-5 w-5 text-mart-wood" /><p className="mt-2 text-2xl font-extrabold text-midnight">{v}</p><p className="text-sm text-muted-foreground">{l}</p></div>
        ))}
      </div>

      <div className="flex gap-2 mb-6">
        <button onClick={() => setTab("requests")} data-testid="vendor-tab-requests" className={`rounded-full px-5 py-2.5 text-sm font-semibold ${tab === "requests" ? "bg-midnight text-white" : "border border-border text-muted-foreground"}`}>Customization Requests</button>
        <button onClick={() => setTab("catalogue")} data-testid="vendor-tab-catalogue" className={`rounded-full px-5 py-2.5 text-sm font-semibold ${tab === "catalogue" ? "bg-midnight text-white" : "border border-border text-muted-foreground"}`}>My Catalogue</button>
      </div>

      {tab === "requests" ? (
        requests.length === 0 ? <p className="text-muted-foreground">No customization requests yet.</p> : (
          <div className="space-y-4" data-testid="vendor-requests-list">{requests.map((r) => <RequestCard key={r.id} req={r} onChange={loadReq} />)}</div>
        )
      ) : <Catalogue vendorName={data.user.name} />}
    </div>
  );
}
